import { useState, useContext, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import styles from './styles/BerechnungsButton.module.css'
import { runSolverJob, cancelSolverJob } from './utils/solverJobClient';
import type { SolverJobResult, SolverJobStatus } from './utils/solverJobClient';
import confettiIcon from './img/party-popper-svgrepo-com.svg';
import ScheduleErrorModal from './components/ScheduleErrorModal';
import type { SolverErrorDetails, SolverRetryOption } from './components/ScheduleErrorModal';
import LongCalculationModal from './components/LongCalculationModal';
import ConfirmModal from './components/ConfirmModal';
import { TimePreferencesContext } from './App';
import { useUnsavedChanges } from './contexts/UnsavedChangesContext';
import type { ApiError } from './utils/apiClient';
import type { DisplaySchedule } from './utils/scheduleHelpers';

/** Neuversuch-Einstellungen aus dem Fehler-Dialog. */
export interface CalculationOverrides {
  wiggleRoom?: number | string;
  allowBlindAssignment?: boolean;
  teacherTimeExtension?: number;
}

export interface BerechnungsButtonHandle {
  trigger: () => void;
}

interface LockedStudent {
  member_id: string;
  day: number;
  time_slots: string[];
  duration?: number | string | null;
}

/** Solver-Job-Ergebnis mit den solver-spezifischen Feldern typisiert. */
type SolverRunOutcome = SolverJobResult & {
  timetable?: unknown;
  message?: string;
  stopped_early?: boolean;
  error_type?: string;
  skipped_students?: Array<string | { name?: string }>;
  unscheduled_students?: Array<string | { name?: string }>;
};

interface SolverStudent {
  member_id: string;
  name?: string | null;
  is_locked?: boolean;
  is_set?: boolean;
  set_day?: number | null;
  duration?: number | string | null;
  [key: string]: unknown;
}

interface BerechnungsButtonProps {
  wiggleRoom: number | string;
  allowBlindAssignment?: boolean;
  teacherTimeExtension?: number;
  advancedWeights?: Record<string, number> | null;
  onCalculationComplete?: (timetable: unknown, lockedStudents: LockedStudent[]) => void;
  isLoading?: boolean;
  selectedClass?: {
    class_id?: string;
    available_times?: Record<string, unknown> | null;
    time_range_for_students?: Record<string, unknown> | null;
    [key: string]: unknown;
  } | null;
  hasData?: boolean;
  onNavigateToStep2?: (() => void) | null;
  studentsList?: SolverStudent[] | null;
  schedule?: DisplaySchedule | null;
  onApplySettings?: (overrides: CalculationOverrides) => void;
}

const BerechnungsButtonCpSolver = forwardRef<BerechnungsButtonHandle, BerechnungsButtonProps>(function BerechnungsButtonCpSolver({ wiggleRoom, allowBlindAssignment = false, teacherTimeExtension = 0, advancedWeights, onCalculationComplete, isLoading, selectedClass, hasData, onNavigateToStep2, studentsList, schedule, onApplySettings }, ref) {
  const { timePreferences } = useContext(TimePreferencesContext);
  const { hasUnsavedChanges } = useUnsavedChanges();
  const [laden, setLaden] = useState(false);
  const [showRecalcConfirm, setShowRecalcConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<SolverErrorDetails | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showCalculationModal, setShowCalculationModal] = useState(false);
  const [showPatienceHint, setShowPatienceHint] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  // Milestone 6: echter Job-Status statt Fake-Fortschrittsbalken
  const [jobStatus, setJobStatus] = useState<SolverJobStatus | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const patienceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleBerechnungRef = useRef<(() => void) | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const jobIdRef = useRef<string | null | undefined>(null);
  useImperativeHandle(ref, () => ({ trigger: () => handleBerechnungRef.current?.() }));

  const clearCalculationTimers = () => {
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
    if (patienceTimerRef.current) {
      clearTimeout(patienceTimerRef.current);
      patienceTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearCalculationTimers();
      // Ohne abort() lief die Poll-Schleife in solverJobClient nach dem Unmount
      // (z.B. Navigation über die Navbar während der Berechnung) minutenlang
      // weiter und rief am Ende Callbacks einer längst verlassenen Closure auf.
      abortRef.current?.abort();
    };
  }, []);

  // Validierungsfunktion für wiggleRoom
  const validateWiggleRoom = (value: number | string | undefined): boolean => {
    // Prüfe ob es eine Zahl ist
    if (typeof value !== 'number' && isNaN(Number(value))) {
      return false;
    }
    
    const numValue = Number(value);
    // Prüfe ob die Zahl im gültigen Bereich liegt (z.B. 0-120 Minuten)
    if (numValue < 0 || numValue > 99) {
      return false;
    }
    
    // Prüfe ob es eine ganze Zahl ist
    if (!Number.isInteger(numValue)) {
      return false;
    }

    return true;
  };

  /**
   * Startet die Berechnung. `overrides` erlaubt einen Neuversuch mit
   * geänderten Einstellungen (aus dem Fehler-Dialog), ohne auf das
   * asynchrone State-Update des Parents warten zu müssen.
   */
  const doBerechnung = async (overrides: CalculationOverrides = {}) => {
    setError(null);
    setErrorDetails(null);
    setShowErrorModal(false);

    const effWiggleRoom = overrides.wiggleRoom ?? wiggleRoom;
    const effBlindAssignment = overrides.allowBlindAssignment ?? allowBlindAssignment;
    const effTeacherTimeExtension = overrides.teacherTimeExtension ?? teacherTimeExtension;

    if (!validateWiggleRoom(effWiggleRoom)) {
      setError('Ungültiger Spielraum-Wert. Bitte geben Sie eine ganze Zahl zwischen 0 und 99 ein.');
      return;
    }
    if (!selectedClass) {
        setError("Bitte wählen Sie zuerst einen Kurs aus.");
        return;
    }

    clearCalculationTimers();
    setLaden(true);
    setShowCalculationModal(true);
    setShowPatienceHint(false);
    setElapsedSeconds(0);
    setJobStatus(null);
    setCancelling(false);
    jobIdRef.current = null;
    abortRef.current = new AbortController();

    const startedAt = Date.now();
    elapsedTimerRef.current = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    patienceTimerRef.current = setTimeout(() => {
      setShowPatienceHint(true);
    }, 60000);

    // Sammle gelockte Studenten
    const lockedStudents: LockedStudent[] = [];
    const completeDaysList = ["MONTAG", "DIENSTAG", "MITTWOCH", "DONNERSTAG", "FREITAG", "SAMSTAG", "SONNTAG"];
    
    if (studentsList && schedule) {
      studentsList.forEach(student => {
        if (student.is_locked && student.is_set) {
          // Sammle die Zeitslots aus dem Schedule
          const dayName = completeDaysList[student.set_day as number];
          const timeSlots: string[] = [];
          
          if (schedule[dayName]) {
            Object.entries(schedule[dayName]).forEach(([time, memberId]) => {
              if (memberId === student.member_id) {
                timeSlots.push(time);
              }
            });
          }
          
          if (timeSlots.length > 0) {
            lockedStudents.push({
              member_id: student.member_id,
              day: student.set_day as number,
              time_slots: timeSlots,
              duration: student.duration
            });
            console.log(`[BerechnungsButtonCpSolver] Locked: ${student.name}, day ${dayName}, slots:`, timeSlots);
          }
        }
      });
    }

    console.log('[BerechnungsButtonCpSolver] Total locked students:', lockedStudents.length);

    // Daten für die Berechnung
    const calculationData = {
      wiggleRoom: Math.floor(Number(effWiggleRoom)),
      selectedClass: selectedClass,
      timePreferences: timePreferences || {},
      locked_students: lockedStudents,
      allow_blind_assignment: !!effBlindAssignment,
      teacher_time_extension_minutes: Number(effTeacherTimeExtension) || 0,
      weights: advancedWeights || null
    };

    try {
      // Milestone 6: Job einreihen und pollen, statt einen HTTP-Request bis zu
      // 130s offen zu halten. Fortschritt kommt jetzt echt aus dem Solver.
      const result = await runSolverJob({
        enqueueUrl: 'solver/jobs',
        body: calculationData,
        classId: selectedClass?.class_id,
        signal: abortRef.current.signal,
        onProgress: (status) => {
          jobIdRef.current = status.job_id;
          setJobStatus(status);
        },
      }) as SolverRunOutcome;

      if (result.status === 'done') {
        if (typeof onCalculationComplete === 'function') {
          onCalculationComplete(result.timetable, lockedStudents);
          // Vorzeitig übernommen: Der Plan ist gültig, aber nicht zu Ende
          // optimiert (weniger kompakte Tage / mehr Lücken als möglich).
          if (result.stopped_early) {
            toast.info('Optimierung vorzeitig beendet – der beste bisher gefundene Stundenplan wurde übernommen.');
          }
        } else {
          console.error("onCalculationComplete is not a function");
        }
      } else if (result.status === 'infeasible') {
        // Kam früher als HTTP 400; jetzt ein terminaler Job-Status.
        setErrorDetails({
          error_type: 'INFEASIBLE',
          message: result.message || 'Stundenplan konnte nicht erstellt werden',
          skipped_students: result.skipped_students || [],
          unscheduled_students: result.unscheduled_students || []
        });
        setShowErrorModal(true);
      } else if (result.status === 'failed') {
        setErrorDetails({
          error_type: result.error_type || 'NETWORK',
          message: result.message || 'Fehler bei der Berechnung',
          skipped_students: [],
          unscheduled_students: []
        });
        setShowErrorModal(true);
      }
      // 'cancelled' -> stillschweigend schließen (der Nutzer wollte das so)

    } catch (caught) {
      const error = caught as ApiError;
      console.error('Fehler bei der Berechnung:', error);

      if (error.name === 'TimeoutError' || error.isTimeout) {
        setErrorDetails({
          error_type: "TIMEOUT",
          message: "Die Berechnung hat das Zeitlimit von 2 Minuten erreicht. Bitte versuche es erneut oder vereinfache die Einstellungen.",
          skipped_students: [],
          unscheduled_students: []
        });
        setShowErrorModal(true);
        return;
      }

      if (error.name === 'AbortError') {
        console.log('[BerechnungsButtonCpSolver] Berechnung wurde abgebrochen.');
        return;
      }

      if (error.status === 429) {
        setErrorDetails({
          error_type: "QUEUE_FULL",
          message: error.originalErrorData?.detail?.message
            || 'Zurzeit laufen viele Berechnungen. Bitte in ein paar Minuten erneut versuchen.',
          skipped_students: [],
          unscheduled_students: []
        });
        setShowErrorModal(true);
        return;
      }

      setErrorDetails({
        error_type: error.status === 401 ? "UNAUTHORIZED" : "NETWORK",
        message: error.message || 'Fehler bei der Berechnung',
        skipped_students: [],
        unscheduled_students: []
      });
      setShowErrorModal(true);
    } finally {
      clearCalculationTimers();
      setShowCalculationModal(false);
      setLaden(false);
      setJobStatus(null);
      setCancelling(false);
      abortRef.current = null;
    }
  };

  // Beide Wege stoppen die Suche sofort (ein weggeklickter Job würde den Worker
  // sonst bis zu 120s für alle anderen Lehrer blockieren). Das Polling läuft in
  // BEIDEN Fällen weiter — der Job wird terminal, und der Client holt entweder
  // das Ergebnis ('done') oder sieht 'cancelled' und schließt still.

  /** Suche beenden und den besten bisher gefundenen Plan übernehmen. */
  const handleAcceptBest = () => {
    setCancelling(true);
    cancelSolverJob(jobIdRef.current, { discard: false });
  };

  /** Abbrechen und nichts übernehmen — auch wenn schon ein Plan existiert. */
  const handleDiscard = () => {
    setCancelling(true);
    cancelSolverJob(jobIdRef.current, { discard: true });
  };

  const handleBerechnung = () => {
    if (hasData && hasUnsavedChanges) {
      setShowRecalcConfirm(true);
      return;
    }
    doBerechnung();
  };
  handleBerechnungRef.current = handleBerechnung;

  /**
   * Schätzt, wie viele Personen unverplant bleiben müssten, damit der Rest in
   * die Lehrerzeiten passt.
   *
   * Nötig, weil der Solver bei INFEASIBLE keine "unscheduled"-Liste liefert (er
   * hat ja gar keinen Plan gefunden) — ein Vorschlag "Spielraum +1" würde dann
   * oft wieder scheitern. Rechnung: Gesamtbedarf (Summe der Unterrichtszeiten)
   * gegen Kapazität (freie Lehrer-Slots); die längsten Termine fallen zuerst
   * weg, bis es passt.
   */
  const estimateRequiredWiggle = (details: SolverErrorDetails): number => {
    const skipped = details.skipped_students?.length || 0;
    const unscheduled = details.unscheduled_students?.length || 0;
    const fallback = skipped + unscheduled;

    const teacherTimes = selectedClass?.available_times || selectedClass?.time_range_for_students;
    if (!teacherTimes || !Array.isArray(studentsList) || studentsList.length === 0) {
      return fallback;
    }

    const capacitySlots = Object.entries(teacherTimes)
      .filter(([day]) => /^\d$/.test(day))          // "mode"-Key ignorieren
      .reduce((sum, [, slots]) => sum + (Array.isArray(slots) ? slots.length : 0), 0);
    if (!capacitySlots) return fallback;

    const demands = studentsList
      .filter((s) => !s.is_locked)
      .map((s) => Math.max(1, Math.ceil((Number(s.duration) || 30) / 15)))
      .sort((a, b) => b - a);                        // längste zuerst rauswerfen

    let demand = demands.reduce((sum, d) => sum + d, 0);
    let mustDrop = 0;
    for (const d of demands) {
      if (demand <= capacitySlots) break;
      demand -= d;
      mustDrop += 1;
    }
    return Math.max(fallback, mustDrop);
  };

  /**
   * Leitet aus dem Solver-Fehler konkrete Neuversuche ab — statt dem Lehrer
   * nur zu sagen, WAS er ändern könnte, bieten wir es direkt an.
   */
  const buildRetryOptions = (details: SolverErrorDetails | null): SolverRetryOption[] => {
    if (!details || details.error_type !== 'INFEASIBLE') return [];

    const skipped = details.skipped_students?.length || 0;
    const currentWiggle = Math.floor(Number(wiggleRoom)) || 0;
    const blocked = estimateRequiredWiggle(details);
    const options: SolverRetryOption[] = [];

    // 1) Spielraum: so viele Personen dürfen unverplant bleiben, dass der Rest
    //    in die verfügbaren Lehrerzeiten passt.
    const suggestedWiggle = Math.min(99, Math.max(currentWiggle + 1, blocked));
    if (suggestedWiggle > currentWiggle && suggestedWiggle <= 99) {
      options.push({
        key: 'wiggle',
        title: `Mit Spielraum ${suggestedWiggle} erneut versuchen`,
        description: blocked > 0
          ? `${blocked === 1 ? 'Eine Person darf' : `${blocked} Personen dürfen`} dann unverplant bleiben – alle anderen bekommen einen Termin.`
          : 'Mehr Schüler:innen dürfen unverplant bleiben, damit der Rest passt.',
        overrides: { wiggleRoom: suggestedWiggle },
      });
    }

    // 2) Eigene Zeiten ausweiten (früher anfangen / später aufhören)
    const currentExtension = Number(teacherTimeExtension) || 0;
    const suggestedExtension = currentExtension + 30;
    if (suggestedExtension <= 120) {
      options.push({
        key: 'extend',
        title: `Deine Zeiten um ${suggestedExtension} Min. ausweiten und erneut versuchen`,
        description: 'Der Solver darf dann entsprechend früher beginnen und später enden.',
        overrides: { teacherTimeExtension: suggestedExtension },
      });
    }

    // 3) Blind-Zuweisung — nur sinnvoll, wenn Leute mangels Zeiten rausflogen
    if (skipped > 0 && !allowBlindAssignment) {
      options.push({
        key: 'blind',
        title: 'Ohne Zeitangaben trotzdem verplanen und erneut versuchen',
        description: `${skipped === 1 ? 'Die Person' : `Die ${skipped} Personen`} ohne eingetragene Verfügbarkeit ${skipped === 1 ? 'wird' : 'werden'} auf beliebige freie Zeiten gelegt.`,
        overrides: { allowBlindAssignment: true },
      });
    }

    return options;
  };

  const handleRetry = (option: SolverRetryOption) => {
    setShowErrorModal(false);
    const overrides = option.overrides as CalculationOverrides;
    // Auch die Regler oben mitziehen, damit der Lehrer sieht, womit gerechnet wird
    if (typeof onApplySettings === 'function') {
      onApplySettings(overrides);
    }
    doBerechnung(overrides);
  };

  // Button-Zustand anpassen (nutzt hasData statt ergebnis)
  const buttonText = hasData ? 'Stundenplan neu berechnen' : 'Stundenplan automatisch erstellen';

  return (
    <div className={styles.buttonContainer}>
      {error && <div className={styles.error}>{error}</div>}
      <button
        // Passe Klassen an (verwende hasData)
        className={`${styles.startButton} ${!hasData ? styles.prominent : styles.lessProminent} ${error ? styles.errorState : ''}`}
        onClick={handleBerechnung}
        disabled={laden || isLoading} // !!error entfernt - Button bleibt klickbar
      >
        {laden ? (
          <>
            <div className={styles.loadingSpinner}></div>
            <span>Berechnung läuft...</span>
          </>
        ) : (
          <>
            <img src={confettiIcon} alt="Confetti" className={styles.confettiIcon} />
            <span>{buttonText}</span>
          </>
        )}
      </button>
      {laden && (
        <div className={styles.loadingMessage}>
          Bitte haben Sie einen Moment Geduld...
        </div>
      )}
       {isLoading && !laden && ( // Zeige globalen Ladezustand, wenn Button nicht klickt
           <div className={styles.loadingMessage}>Lade Daten...</div>
       )}
       
       <LongCalculationModal
         isOpen={showCalculationModal}
         showPatienceHint={showPatienceHint}
         elapsedSeconds={elapsedSeconds}
         jobStatus={jobStatus}
         onAcceptBest={handleAcceptBest}
         onDiscard={handleDiscard}
         cancelling={cancelling}
       />

       <ScheduleErrorModal
         isOpen={showErrorModal}
         onClose={() => setShowErrorModal(false)}
         errorDetails={errorDetails}
         onNavigateToStep2={onNavigateToStep2}
         retryOptions={buildRetryOptions(errorDetails)}
         onRetry={handleRetry}
       />

       <ConfirmModal
         isOpen={showRecalcConfirm}
         onClose={() => setShowRecalcConfirm(false)}
         onConfirm={() => {
           setShowRecalcConfirm(false);
           doBerechnung();
         }}
         title="Ungespeicherte Änderungen überschreiben?"
         message="Der aktuelle Stundenplan hat ungespeicherte Änderungen. Eine Neu-Berechnung überschreibt diese. Fortfahren?"
         buttonText="Neu berechnen"
       />

    </div>
  );
});

export default BerechnungsButtonCpSolver;
